import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { MapPinIcon, PlusIcon } from '@/shared/ui/icons';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import { apiAddressDelete, useAddresses } from '@/entities/addresses';
import { FeaturesAddressesAddressCard } from '@/features/addresses';
import type { ApiAddressesGetItem } from '@/shared/types/api/addresses';

export const WidgetsAddressesMain = () => {
  const { t } = useTranslation('addresses');
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading, isError, error, refetch } = useAddresses();
  const [deleting, setDeleting] = useState<ApiAddressesGetItem | null>(null);

  const remove = useMutate({
    api: (id: string) => apiAddressDelete(id),
    invalidateKeys: [queryKeys.addresses.all],
    onSuccess: () => {
      toast.success(t('toast.deleted'));
      setDeleting(null);
    },
    onError: (_err, message) => toast.error(message),
  });

  const openAdd = (): void => {
    void navigate('/settings/addresses/new');
  };

  const openEdit = (address: ApiAddressesGetItem): void => {
    void navigate(`/settings/addresses/${address.id}/edit`);
  };

  const addresses = data ?? [];

  return (
    <>
      <PageHeader
        title={t('title')}
        showBack
        backTo="/settings"
        right={
          <Button size="sm" onClick={openAdd} leftIcon={<PlusIcon className="h-4 w-4" />}>
            {t('add')}
          </Button>
        }
      />
      <div className="flex flex-col gap-3 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : isError ? (
          <ErrorState message={extractErrorMessage(error)} onRetry={() => void refetch()} />
        ) : addresses.length === 0 ? (
          <EmptyState
            icon={<MapPinIcon className="h-10 w-10" aria-hidden />}
            title={t('emptyTitle')}
            description={t('emptyDescription')}
            action={
              <Button onClick={openAdd} leftIcon={<PlusIcon className="h-4 w-4" />}>
                {t('add')}
              </Button>
            }
          />
        ) : (
          addresses.map((address) => (
            <FeaturesAddressesAddressCard
              key={address.id}
              address={address}
              onEdit={openEdit}
              onDelete={setDeleting}
            />
          ))
        )}
      </div>

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t('confirmDeleteTitle')}
        description={t('confirmDeleteDescription')}
        confirmLabel={t('confirmDeleteAction')}
        confirmVariant="danger"
        loading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
      />
    </>
  );
};
